import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { assessmentSessions } from '../api/client';

const SCORE_OPTIONS = [
  { value: 0, label: 'Not Yet', description: 'Skill not demonstrated', color: 'red' },
  { value: 1, label: 'Emerging', description: 'Beginning to demonstrate skill', color: 'yellow' },
  { value: 2, label: 'Developing', description: 'Skill developing with practice', color: 'blue' },
  { value: 3, label: 'Mastered', description: 'Skill demonstrated consistently', color: 'green' },
];

export default function ReviewDrawingsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScore, setSelectedScore] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    loadReviewData();
  }, [id]);

  const loadReviewData = async () => {
    try {
      setLoading(true);
      const response = await assessmentSessions.getReview(id);
      setReviewData(response.data);

      // Start at first unreviewed response
      const firstUnreviewedIndex = response.data.responses.findIndex(
        (r) => r.teacher_score === null
      );
      setCurrentIndex(firstUnreviewedIndex >= 0 ? firstUnreviewedIndex : 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load review data');
      console.error('Failed to load review:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentResponse = reviewData?.responses[currentIndex];

  const handleScoreSubmit = async () => {
    if (selectedScore === null || !currentResponse) return;

    try {
      setSaving(true);
      const result = await assessmentSessions.scoreResponse(
        id,
        currentResponse.id,
        selectedScore,
        notes || null
      );

      // Update local state
      setReviewData((prev) => ({
        ...prev,
        pending_count: result.data.pending_count,
        reviewed_count: result.data.reviewed_count,
        responses: prev.responses.map((r) =>
          r.id === currentResponse.id
            ? { ...r, teacher_score: result.data.teacher_score, teacher_notes: result.data.teacher_notes }
            : r
        ),
      }));

      // Move to next unreviewed
      const nextUnreviewedIndex = reviewData.responses.findIndex(
        (r, i) => i > currentIndex && r.teacher_score === null
      );

      if (nextUnreviewedIndex >= 0) {
        setCurrentIndex(nextUnreviewedIndex);
        setSelectedScore(null);
        setNotes('');
      } else {
        // All reviewed, check if we can finalize
        const allReviewed = reviewData.responses.every(
          (r) => r.id === currentResponse.id || r.teacher_score !== null
        );
        if (allReviewed) {
          // Prompt to finalize
          setSelectedScore(null);
          setNotes('');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save score');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      await assessmentSessions.finalizeReview(id);
      navigate(`/app/assessments/${id}/results`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to finalize review');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <Link
          to="/app/reviews"
          className="mt-2 inline-block text-sm text-red-600 hover:underline"
        >
          Back to Pending Reviews
        </Link>
      </div>
    );
  }

  if (!reviewData) return null;

  const allReviewed = reviewData.responses.every((r) => r.teacher_score !== null);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review Assessment</h1>
          <p className="text-sm text-gray-500">
            {reviewData.student_name} - {reviewData.template_name}
          </p>
        </div>
        <Link
          to="/app/reviews"
          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
        >
          Back to Pending Reviews
        </Link>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Review Progress</span>
          <span className="text-sm text-gray-500">
            {reviewData.reviewed_count} of {reviewData.responses.length} reviewed
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-indigo-600 h-2 rounded-full transition-all"
            style={{
              width: `${(reviewData.reviewed_count / reviewData.responses.length) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Finalize button if all reviewed */}
      {allReviewed && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-green-800">All responses reviewed!</h3>
              <p className="text-sm text-green-600">
                Click finalize to complete the assessment and calculate results.
              </p>
            </div>
            <button
              onClick={handleFinalize}
              disabled={finalizing}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {finalizing ? 'Finalizing...' : 'Finalize Review'}
            </button>
          </div>
        </div>
      )}

      {/* Current response to review */}
      {currentResponse && !allReviewed && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Response navigation */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
            <button
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className="text-gray-600 hover:text-gray-900 disabled:opacity-30"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Response {currentIndex + 1} of {reviewData.responses.length}
            </span>
            <button
              onClick={() =>
                setCurrentIndex(Math.min(reviewData.responses.length - 1, currentIndex + 1))
              }
              disabled={currentIndex === reviewData.responses.length - 1}
              className="text-gray-600 hover:text-gray-900 disabled:opacity-30"
            >
              Next
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Question prompt */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Question</h3>
              <p className="text-gray-700">{currentResponse.question_prompt}</p>
              {currentResponse.question_config?.expected && (
                <p className="text-sm text-gray-500 mt-1">
                  Expected: <span className="font-medium">{currentResponse.question_config.expected}</span>
                </p>
              )}
            </div>

            {/* Student's drawing */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Student's Answer</h3>
              {currentResponse.answer ? (
                <div className="border-2 border-gray-200 rounded-lg p-2 inline-block bg-white">
                  <img
                    src={currentResponse.answer}
                    alt="Student's drawing"
                    className="max-w-full max-h-96 object-contain"
                  />
                </div>
              ) : (
                <p className="text-gray-500 italic">No response provided</p>
              )}
            </div>

            {/* Already reviewed indicator */}
            {currentResponse.teacher_score !== null && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">
                  Already scored: <strong>{SCORE_OPTIONS[currentResponse.teacher_score]?.label}</strong>
                </p>
                {currentResponse.teacher_notes && (
                  <p className="text-sm text-green-600 mt-1">Notes: {currentResponse.teacher_notes}</p>
                )}
              </div>
            )}

            {/* Scoring interface */}
            {currentResponse.teacher_score === null && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Score this response</h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {SCORE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSelectedScore(option.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        selectedScore === option.value
                          ? `border-${option.color}-500 bg-${option.color}-50`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.description}</div>
                    </button>
                  ))}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Add any notes about this response..."
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>

                {/* Submit button */}
                <button
                  onClick={handleScoreSubmit}
                  disabled={selectedScore === null || saving}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {saving ? 'Saving...' : 'Save Score & Continue'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
